import type { DraftWithLead, Lead } from '../../../shared/types';
import { shell } from '../app';
import { copyText, createDraft, listDrafts, setDraftStatus } from '../data/drafts';
import type { ManualTrigger } from '../data/drafts';
import { timeAgo } from '../logic/dates';
import { btn, linkBtn, section } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';

const DRAFT_LABEL: Record<string, string> = {
  contract_expiring: 'Contract',
  post_visit: 'Follow-up',
  meeting_confirm: 'Meeting',
  feature_launched: 'Feature news',
};

const draftLabel = (d: { trigger: string; milestone_key: string | null }): string => {
  if (d.milestone_key === 'contract_30') return 'Contract · 30d';
  if (d.milestone_key === 'contract_60') return 'Contract · 60d';
  return DRAFT_LABEL[d.trigger] ?? d.trigger;
};

export interface LeadDraftsPanel {
  /** Fetch this lead's drafts from the API and repaint the list (a failed refresh keeps the list). */
  load: () => Promise<void>;
  /** The section for the current lead; the list inside keeps its content across page re-renders. */
  section: (lead: Lead) => HTMLElement;
}

/**
 * Email drafts for ONE lead (D41): existing drafts (tap → copy) + the manual triggers. Drafts are
 * online-backed (D33), so the panel loads on its own and outlives page re-renders: the lead page
 * paints straight from the mirror instead of waiting on the network, and a refresh never collapses
 * the draft being read.
 */
export const leadDraftsPanel = (leadId: string): LeadDraftsPanel => {
  const listHost = h(
    'div',
    { class: 'card leaddrafts' },
    h('p', { class: 'muted small' }, 'Loading drafts…'),
  );
  let drafts: DraftWithLead[] | null = null; // null until the first successful load
  let openId: string | null = null;
  let loadSeq = 0;

  const draftRow = (d: DraftWithLead) =>
    h(
      'div',
      { class: 'leaddraft' },
      h(
        'button',
        {
          type: 'button',
          class: 'leaddraft__row',
          onClick: () => {
            openId = openId === d.id ? null : d.id;
            paint();
          },
        },
        span('posbadge', draftLabel(d)),
        d.status !== 'pending' ? span(`posbadge draft__status--${d.status}`, d.status) : null,
        span('leaddraft__subject', d.subject),
        span('mono muted small', timeAgo(d.created_at)),
      ),
      openId === d.id
        ? h(
            'div',
            { class: 'leaddraft__body' },
            h('pre', { class: 'draft__text' }, d.body),
            h(
              'div',
              { class: 'stack-h' },
              btn('Copy both', {
                variant: 'primary',
                size: 'sm',
                onClick: async () => {
                  const ok = await copyText(`Subject: ${d.subject}\n\n${d.body}`);
                  if (!ok) {
                    shell.toast('Could not copy — open it in Emails instead', { kind: 'error' });
                    return;
                  }
                  shell.toast('Email copied — paste it into your work email', { kind: 'success' });
                  if (d.status === 'pending') {
                    await setDraftStatus(d.id, 'copied').catch(() => undefined);
                    await load();
                  }
                },
              }),
              linkBtn('Open in Emails', '#/emails', { variant: 'subtle', size: 'sm' }),
            ),
          )
        : null,
    );

  const paint = () =>
    replaceChildren(
      listHost,
      drafts?.length ? null : h('p', { class: 'muted small' }, 'No drafts for this lead yet.'),
      (drafts ?? []).map(draftRow),
    );

  const load = async () => {
    const mine = ++loadSeq;
    try {
      const fresh = await listDrafts({ lead_id: leadId });
      if (mine !== loadSeq) return; // a newer load is in charge
      drafts = fresh;
      paint();
    } catch {
      // A failed refresh keeps the list already shown; only an empty panel explains itself.
      if (mine === loadSeq && drafts === null) {
        replaceChildren(listHost, h('p', { class: 'muted small' }, 'Email drafts need a connection.'));
      }
    }
  };

  const makeBtn = (label: string, trigger: ManualTrigger) =>
    btn(label, {
      variant: 'subtle',
      size: 'sm',
      onClick: async (ev) => {
        const b = ev.currentTarget as HTMLButtonElement;
        b.disabled = true;
        try {
          await createDraft(leadId, trigger);
          shell.toast('Draft ready below', { kind: 'success' });
          openId = null;
          await load();
        } catch (err) {
          shell.toast(err instanceof Error ? err.message : String(err), { kind: 'error' });
        } finally {
          b.disabled = false;
        }
      },
    });

  return {
    load,
    section: (lead) =>
      section(
        { title: 'Email drafts', action: linkBtn('Emails', '#/emails', { variant: 'subtle', size: 'sm' }) },
        listHost,
        h(
          'div',
          { class: 'stack-h' },
          lead.contract_end_date ? makeBtn('Contract draft', 'contract_expiring') : null,
          makeBtn('Follow-up', 'post_visit'),
          makeBtn('Meeting confirm', 'meeting_confirm'),
        ),
      ),
  };
};
